import produce from 'immer';
import { getFlagOverrideValue } from 'src/utils/featureFlags';
import { create } from 'zustand';

// Base types for feature flags
export interface BaseFeatureFlag {
	/** @private Internal flag state - use flagEnabled() instead */
	readonly _enabled: boolean;
	rawPayload?: unknown;
	hasBeenSynced: boolean;
}

export interface BaseFeatureFlagWithPayload<T> extends BaseFeatureFlag {
	readonly payload: T;
}

// Special feature flags that need custom handling
export interface HighlightedAssetsFlag extends BaseFeatureFlag {
	assets: string[];
}
export interface SpotMarketsFlag extends BaseFeatureFlag {
	markets: string[];
}
export interface Maker20kRewardMarketIndexesFlag extends BaseFeatureFlag {
	marketIndexes: number[];
}
export interface IsolatedMarginEnabledMarketsFlag extends BaseFeatureFlag {
	marketSymbols: string[];
}

// Define the feature flag configurations
export interface FeatureFlagConfigs {
	SKIP_TRANSACTION_PREFLIGHTS: BaseFeatureFlag;
	HIGHLIGHTED_ASSETS: HighlightedAssetsFlag;
	ENABLE_UI_INVARIANT_CHECKS: BaseFeatureFlag;
	// Add new feature flags here by extending this interface
	// NEW_FEATURE_FLAG: BaseFeatureFlag;
	ENABLE_SIGNED_MSG_ORDERS: BaseFeatureFlag;
	ENABLE_WS_DRIFT_CLIENT_ACCOUNT_SUBSCRIBER_V2: BaseFeatureFlag;
	DISABLE_BLOCKCHAIN_EVENTS_FOR_MARKET_ORDER_TOASTS: BaseFeatureFlag;
	DISABLE_DATA_API_EVENTS_FOR_MARKET_ORDER_TOASTS: BaseFeatureFlag;
	DISABLE_ACCOUNT_ORDER_STATE_TOAST_SYNCING: BaseFeatureFlag;
	DLOG_CONSOLE_ENABLED: BaseFeatureFlag;
	SHOWN_SPOT_MARKETS: SpotMarketsFlag;
	ENABLE_INDICATIVE_ORDERBOOK: BaseFeatureFlag;
	ENABLE_EXPONENT_POOL_REWARDS: BaseFeatureFlag;
	ENABLE_VERIFIED_VAULTS: BaseFeatureFlag;
	FORCE_ORDER_PARAMS_FALLBACK: BaseFeatureFlag;
	MAKER_20K_REWARD_MARKET_INDEXES: Maker20kRewardMarketIndexesFlag;
	ENABLE_POSITION_DELTA_TOAST_MATCHING: BaseFeatureFlag;
	AUCTION_PARAMS_VERSION: BaseFeatureFlag;
	DISABLE_WEBSOCKET_DLOB_SOURCE: BaseFeatureFlag;
	ENABLE_EMBEDDED_WALLET: BaseFeatureFlag;
	ENABLE_PRIVY_NATIVE_ACCOUNTS: BaseFeatureFlag;
	DISABLE_TRANSACTION_AUTO_SIGNING: BaseFeatureFlag;
	ENABLE_ISOLATED_POSITIONS: BaseFeatureFlag;
	ISOLATED_MARGIN_ENABLED_MARKETS: IsolatedMarginEnabledMarketsFlag;
	/** Deposit modal "Deposit from other sources" (Fun.xyz). Use PostHog Release conditions to enable for select users. */
	ENABLE_DEPOSIT_FROM_OTHER_SOURCES: BaseFeatureFlag;
	OLD_GRAFANA_METRICS_TO_POSTHOG_ENABLED: BaseFeatureFlag;
	/** Elfa Apollo AI widget. Use PostHog Release conditions to enable progressively. */
	ENABLE_ELFA_WIDGET: BaseFeatureFlag;
}

// Derive the feature flag key type from the configs
export type FeatureFlagKey = keyof FeatureFlagConfigs;

// Type helper to get the flag type for a specific key
export type FlagTypeForKey<K extends FeatureFlagKey> = FeatureFlagConfigs[K];

// The store's flag state type
type FlagState = {
	[K in FeatureFlagKey]: FlagTypeForKey<K>;
};

// Default values for all feature flags
export const FEATURE_FLAG_DEFAULTS: FlagState = {
	SKIP_TRANSACTION_PREFLIGHTS: {
		_enabled: false,
		hasBeenSynced: false,
	},
	HIGHLIGHTED_ASSETS: {
		_enabled: false,
		assets: [],
		hasBeenSynced: false,
	},
	ENABLE_UI_INVARIANT_CHECKS: {
		_enabled: false,
		hasBeenSynced: false,
	},
	ENABLE_SIGNED_MSG_ORDERS: {
		_enabled: true,
		hasBeenSynced: false,
	},
	ENABLE_WS_DRIFT_CLIENT_ACCOUNT_SUBSCRIBER_V2: {
		_enabled: false,
		hasBeenSynced: false,
	},
	DISABLE_BLOCKCHAIN_EVENTS_FOR_MARKET_ORDER_TOASTS: {
		_enabled: false,
		hasBeenSynced: false,
	},
	DISABLE_DATA_API_EVENTS_FOR_MARKET_ORDER_TOASTS: {
		_enabled: false,
		hasBeenSynced: false,
	},
	DISABLE_ACCOUNT_ORDER_STATE_TOAST_SYNCING: {
		_enabled: false,
		hasBeenSynced: false,
	},
	DLOG_CONSOLE_ENABLED: {
		_enabled: false,
		hasBeenSynced: false,
	},
	SHOWN_SPOT_MARKETS: {
		_enabled: false,
		markets: ['SOL', 'DRIFT'],
		hasBeenSynced: false,
	},
	ENABLE_INDICATIVE_ORDERBOOK: {
		_enabled: true,
		hasBeenSynced: false,
	},
	ENABLE_EXPONENT_POOL_REWARDS: {
		_enabled: false,
		hasBeenSynced: false,
	},
	ENABLE_VERIFIED_VAULTS: {
		_enabled: false,
		hasBeenSynced: false,
	},
	FORCE_ORDER_PARAMS_FALLBACK: {
		_enabled: false,
		hasBeenSynced: false,
	},
	MAKER_20K_REWARD_MARKET_INDEXES: {
		_enabled: false,
		marketIndexes: [13, 71, 23, 4, 75, 59, 9, 7, 30, 62], // Default to the same values as REWARD_MARKETS_20K
		hasBeenSynced: false,
	},
	ENABLE_POSITION_DELTA_TOAST_MATCHING: {
		_enabled: false,
		hasBeenSynced: false,
	},
	AUCTION_PARAMS_VERSION: {
		_enabled: false,
		hasBeenSynced: false,
	},
	DISABLE_WEBSOCKET_DLOB_SOURCE: {
		_enabled: false,
		hasBeenSynced: false,
	},
	ENABLE_EMBEDDED_WALLET: {
		_enabled: false,
		hasBeenSynced: false,
	},
	ENABLE_PRIVY_NATIVE_ACCOUNTS: {
		_enabled: false,
		hasBeenSynced: false,
	},
	DISABLE_TRANSACTION_AUTO_SIGNING: {
		_enabled: false,
		hasBeenSynced: false,
	},
	ENABLE_ISOLATED_POSITIONS: {
		_enabled: false,
		hasBeenSynced: false,
	},
	ISOLATED_MARGIN_ENABLED_MARKETS: {
		_enabled: false,
		marketSymbols: ['SOL-PERP', 'ETH-PERP', 'BTC-PERP'],
		hasBeenSynced: false,
	},
	ENABLE_DEPOSIT_FROM_OTHER_SOURCES: {
		_enabled: false,
		hasBeenSynced: false,
	},
	OLD_GRAFANA_METRICS_TO_POSTHOG_ENABLED: {
		_enabled: false,
		hasBeenSynced: false,
	},
	ENABLE_ELFA_WIDGET: {
		_enabled: false,
		hasBeenSynced: false,
	},
};

export interface FeatureFlagStore {
	set: (x: (s: FeatureFlagStore) => void) => void;
	get: () => FeatureFlagStore;
	flags: FlagState;
	customOverrides: Set<FeatureFlagKey>; // Custom overrides from API
	flagEnabled: (flagKey: FeatureFlagKey, skipOverride?: boolean) => boolean;
	getFlagPayload: <K extends FeatureFlagKey>(
		flagKey: K
	) => FlagTypeForKey<K>['rawPayload'];
}

const useFeatureFlagStore = create<FeatureFlagStore>((set, get) => ({
	set: (fn) => set(produce(fn)),
	get: () => get(),
	flags: FEATURE_FLAG_DEFAULTS,
	customOverrides: new Set<FeatureFlagKey>(),
	flagEnabled: (flagKey: FeatureFlagKey, ignoreOverride?: boolean) => {
		const baseValue = get().flags[flagKey]?._enabled ?? false;

		if (ignoreOverride) return baseValue;

		// Priority 1: localStorage override (dev override - highest priority)
		const flagOverride = getFlagOverrideValue(flagKey);
		if (flagOverride !== undefined) return flagOverride;

		// Priority 2: Custom API override
		const customOverrides = get().customOverrides;
		if (customOverrides.has(flagKey)) {
			// Custom override enables the flag regardless of base value
			return true;
		}

		// Priority 3: Base PostHog value (lowest priority)
		return baseValue;
	},
	getFlagPayload: <K extends FeatureFlagKey>(flagKey: K) =>
		get().flags[flagKey]?.rawPayload,
}));

export default useFeatureFlagStore;
